import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Play, ExternalLink, Image } from 'lucide-react';

export default function PortfolioGrid({ items, emptyIcon: EmptyIcon, emptyLabel, isOwnProfile, dashboardPath }) {
  const navigate = useNavigate();
  const [lightboxUrl, setLightboxUrl] = useState(null);

  if (items.length === 0) {
    const Icon = EmptyIcon || Image;
    return (
      <Card className="glass-card border-border p-12 text-center">
        <Icon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-medium text-lg mb-2">{emptyLabel || 'No portfolio items yet'}</h3>
        <p className="text-muted-foreground">
          {isOwnProfile ? 'Add your work from your dashboard to showcase here.' : 'Nothing to show yet.'}
        </p>
        {isOwnProfile && dashboardPath && (
          <Button className="mt-4" onClick={() => navigate(dashboardPath)}>Add Portfolio Item</Button>
        )}
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="portfolio-grid">
        {items.map(item => {
          const thumb = item.thumbnail_url || item.media_url;
          const isVideo = item.media_type === 'video';
          return (
            <div
              key={item.item_id}
              className="group relative aspect-square rounded-xl overflow-hidden bg-muted cursor-pointer"
              onClick={() => {
                if (item.external_url) window.open(item.external_url, '_blank');
                else if (thumb) setLightboxUrl(thumb);
              }}
              data-testid={`portfolio-item-${item.item_id}`}
            >
              {thumb ? (
                <img src={thumb} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-10 h-10 text-muted-foreground/80" />
                </div>
              )}

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-3 text-center">
                {isVideo && <Play className="w-8 h-8 text-foreground mb-2" />}
                <p className="text-white font-medium text-sm line-clamp-2">{item.title}</p>
                {item.category && <Badge variant="secondary" className="mt-1 text-xs">{item.category}</Badge>}
                {item.external_url && <ExternalLink className="w-4 h-4 text-muted-foreground/80 mt-2" />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 z-50 bg-background/90 flex items-center justify-center p-4" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        </div>
      )}
    </>
  );
}
